import React from "react";
import { Helmet } from "react-helmet-async";
import "./appointment.css";
import HeroBanner from "../Hero/HeroBanner";
import heroImg from "../../../assets/medical/appointment/idopontfoglalas_banner_nagy.png";
import calendarImg from "../../../assets/medical/appointment/calendar.png";
import phoneImg from "../../../assets/medical/appointment/phone-call.png";
import landlinePhoneImg from "../../../assets/medical/appointment/telephone.png";
import emailImg from "../../../assets/medical/appointment/email.png";
import locationImg from "../../../assets/medical/appointment/maps-and-flags.png";

const Appointment = () => {
  return (
    <>
      <Helmet>
        <title>
          Időpontfoglalás laborvizsgálatokra | Bálint Analitika Budapest | 11. kerület
        </title>
        <meta
          name="description"
          content="Foglaljon időpontot laborvizsgálatainkra online, telefonon vagy e-mailben a Bálint Analitika budapesti laboratóriumába. Vérleletek, laboreredmények gyorsan és pontosan."
        />
        <meta
          name="keywords"
          content="időpontfoglalás, laborvizsgálat időpont, orvosi laborvizsgálat Budapest, vérleletek, Bálint Analitika"
        />
        <link
          rel="canonical"
          href="https://balintanalitika.hu/orvosdiagnosztika/idopontfoglalas"
        />

        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalBusiness",
            name: "Bálint Analitika Laboratórium",
            url: "https://balintanalitika.hu/orvosdiagnosztika/idopontfoglalas",
            telephone: "+36-1-205-3044",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Kondorfa utca 6.",
              addressLocality: "Budapest",
              postalCode: "1116",
              addressRegion: "XI. kerület",
              addressCountry: "HU",
            },
            openingHoursSpecification: [
              {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: [
                  "Monday",
                  "Tuesday",
                  "Wednesday",
                  "Thursday",
                  "Friday",
                ],
                opens: "07:00",
                closes: "15:00",
              },
            ],
          })}
        </script>
      </Helmet>

      <HeroBanner
        srcBig={heroImg}
        srcSmall={heroImg}
        alt="Időpontfoglalás a Bálint Analitika laboratóriumba"
      />

      <main className="appointment-container container">
        <h1 className="visually-hidden">
          Időpontfoglalás
        </h1>

        <section aria-labelledby="appointment-options">
          <h2 id="appointment-options" className="heading-tertiary">
            Időpontfoglalásra a következő módokon van lehetőség:
          </h2>

          <p className="appointment-info-text">
            <strong>
              Az online időpontfoglaló rendszeren keresztül a nap 24 órájában
              foglalhat vizsgálatainkra időpontot, míg a telefonos és e-mail-es
              időpontfoglalás csak munkanapokon 8:00-15:00 között lehetséges.
              Laboratóriumunk Budapest XI. kerületében, a Kondorfa utca 6. szám
              alatt található. A vizsgálatokra való felkészülésről{" "}
              <a
                href="/orvosdiagnosztika/vizsgalatok/mintavetel"
                title="Laborvizsgálatra való felkészülési útmutató"
                className="appointment-link"
              >
                <strong>részletes tájékoztatónkat itt olvashatja</strong>
              </a>
              .
            </strong>
          </p>

          <p className="appointment-info-text">
            Kérjük, hogy időben érkezzen meg a vizsgálatra, mivel a késés a
            vizsgálatokat és a következő páciensek időpontját is
            befolyásolhatja. Amennyiben a vizsgálatra nem tud megjelenni, azt
            legkésőbb 24 órával a vizsgálat előtt jelezze felénk, hogy a
            vizsgálati időpontot más páciensnek tudjuk biztosítani.
          </p>

          <div className="appointment-wrapper">
            <ul
              className="appointment-list"
              aria-label="Időpontfoglalási lehetőségek"
            >
              <li className="appointment-list-item">
                <p className="appointment-text">
                  Online időpontfoglaló rendszerünkön:
                </p>
                <a
                  href="https://balintanalitika.booked4.us"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="btn btn-lg appointment-btn"
                  aria-label="Online időpontfoglalás a Bálint Analitikához"
                >
                  <span aria-hidden="true">
                    <img
                      src={calendarImg}
                      alt="Naptár ikon"
                      className="appointment-btn-icon"
                      width="24"
                      height="24"
                      loading="lazy"
                    />
                  </span>
                  Online foglalok
                </a>
              </li>

              <li className="appointment-list-item">
                <p className="appointment-text">Mobiltelefonszámunkon:</p>
                <a
                  href="tel:+36202542591"
                  className="btn btn-sm appointment-btn"
                  aria-label="Hívás a mobil telefonszámon: 06 20 254 2591"
                >
                  <span aria-hidden="true">
                    <img
                      src={phoneImg}
                      alt="Mobiltelefon ikon"
                      className="appointment-btn-icon"
                      width="24"
                      height="24"
                      loading="lazy"
                    />
                  </span>
                  Telefonálok
                </a>
              </li>

              <li className="appointment-list-item">
                <p className="appointment-text">Vezetékes elérhetőségünkön: </p>
                <a
                  href="tel:+3612053044"
                  className="btn btn-sm appointment-btn"
                  aria-label="Hívás a vezetékes telefonszámon: 06 1 205 3044"
                >
                  <span aria-hidden="true">
                    <img
                      src={landlinePhoneImg}
                      alt="Vezetékes telefon ikon"
                      className="appointment-btn-icon"
                      width="24"
                      height="24"
                      loading="lazy"
                    />
                  </span>
                  Telefonálok
                </a>
              </li>

              <li className="appointment-list-item">
                <p className="appointment-text">E-mail-en: </p>
                <a
                  href="mailto:orvosdiagnosztika@balintanalitika.hu"
                  className="btn btn-sm appointment-btn"
                  aria-label="Email küldése az orvosdiagnosztika@balintanalitika.hu címre"
                >
                  <span aria-hidden="true">
                    <img
                      src={emailImg}
                      alt="Email ikon"
                      className="appointment-btn-icon"
                      width="24"
                      height="24"
                      loading="lazy"
                    />
                  </span>
                  E-mail-t írok
                </a>
              </li>

              <li className="appointment-list-item">
                <p className="appointment-text">Helyszín: </p>
                <a
                  href="https://maps.app.goo.gl/1fHpr1EnwQT4j3Wy6"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="btn btn-sm appointment-btn"
                  aria-label="Laboratóriumunk helyszíne: Budapest XI. kerület, Kondorfa utca 6."
                >
                  <span aria-hidden="true">
                    <img
                      src={locationImg}
                      alt="Helyszín ikon"
                      className="appointment-btn-icon"
                      width="24"
                      height="24"
                      loading="lazy"
                    />
                  </span>
                  Kondorfa utca 6.
                </a>
              </li>
            </ul>
          </div>
        </section>
      </main>
    </>
  );
};

export default Appointment;
